import _ from 'lodash'
import React, { useCallback, useRef } from 'react';
import ActionMenuV2 from "components/ActionMenuV2";
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import ModalUtil from 'utils/modalUtil';
import { CellRenderers } from 'utils/tableUtils';
import { InfoIcon } from 'components/Icons';
import Vocabulary from 'components/Vocabulary';
import { PermissionSetMap } from 'constants/RBACConstants';
import message from "components/CustomMessage";
import RepoMappingForm from './RepoMappingForm';
import CodeInsightsModal from 'views/Integrations/github/CodeInsightsModal';
import TableContainerEvolved from 'components/TableContainerEvolved';

export default function RepositoryExpand({ installation, toolName }) {
    const { id, hostUrl } = installation || {};
    const tableRef = useRef();

    const fetchRepoListing = useCallback((params) => {
        const queryParams = {
            ..._.omit(params,["filters"]),
            ...params.filters
        }
        if (!_.isEmpty(queryParams.product)) {
            queryParams.productId = queryParams.product
            delete queryParams.product
        }
        if (!_.isEmpty(queryParams.subproduct)) {
            queryParams.subProductId = queryParams.subproduct
            delete queryParams.subproduct
        }
        const { promise, cancel } = ToolsIntegrationsAPI.getRepoDetails(id, queryParams);
        return {promise, cancel}
    }, [id])

    const fetchRepoFilter = useCallback((params) => {
        return ToolsIntegrationsAPI.getRepoFilters(id, params);
    }, [id])

    const editRepositoryForm = (repository) => {
        ModalUtil.show({
            content: <RepoMappingForm configData={repository} toolName={toolName} installation={installation} hostUrl={hostUrl}/>,
            afterClose: () => {
                tableRef.current.refresh();
                tableRef.current.refreshFilters();
            }
        });
    }

    const deleteRepoMapping = async (id) => {
        try{
            await ToolsIntegrationsAPI.deleteGitMapping(id).promise;
            message.success("Deleted successfully");
            tableRef.current.refresh();
            tableRef.current.refreshFilters();
        } catch (err) {
            console.error(err);
            throw err;
        }
    }

    const productVocab = <Vocabulary name="product" />;
    const subProductVocab = <Vocabulary name="subProduct" />;

    const items = (record) => [{
        type: "edit",
        text: !record.subProductName && "Assign",
        tooltip: "Edit Repository details"
    },
    ...(toolName==="GitLab" ? [{
        type: "codeInsights",
        text: "Code Insights",
        icon: <InfoIcon/>,
        tooltip: record?.productId ? "" : "Repository must be mapped to enable Code Insights",
        disabled: !record?.productId,
    }] : []),
    {
        type: "delete",
        text:"Delete Mapping",
        permission: PermissionSetMap.DELETE_TOOL,
        tooltip: record?.productId ? <>Delete {productVocab}/{subProductVocab} mapping</> : "No mapping available to delete",
        disabled: !record?.productId,
        promptMessage: <>This will delete repository {productVocab}/{subProductVocab} mapping.</>

    }]

    const onClick = async (_, record) => {
        const {data} = record
        switch(record.name){
            case "edit": 
                editRepositoryForm(data)
                break;
            case "delete":
                await deleteRepoMapping(data?.id)
                break;
            case "codeInsights":
                ModalUtil.show({
                    content: <CodeInsightsModal repository={data} installation={installation} />,
                    onClose: () => {
                        tableRef.current?.refresh();
                        tableRef.current?.refreshFilters();
                    }
                });
                break;
            default:
                break;
        }
    }

    const getTableRef = useCallback((ref) => {
        tableRef.current = ref;
    }, []);

    const columns = [{
        title: "Name",
        dataIndex: "name",
        width: 150,
        showFilter: true,
        onlySearch: true
    }, {
        title: "Created On",
        dataIndex: toolName === "Azure_Repos" ? "createdAt" : "repoCreatedAt",
        key: toolName === "Azure_Repos" ? "createdAt" : "repoCreatedAt",
        showSort: true,
        render: CellRenderers.TIME_RELATIVE,
        width: 100
    }, {
        title: "Updated On",
        dataIndex: "repoUpdatedAt",
        key: "repoUpdatedAt",
        width: 100,
        showSort: true,
        render: CellRenderers.TIME_RELATIVE
    },{
        title: productVocab,
        dataIndex: "product",
        render:(product,record) => CellRenderers.VALUE_OR_NA(record.productName),
        showFilter: true,
        width: 100

    }, 
    {
        title: subProductVocab,
        dataIndex: "subproduct",
        render:(subproduct,record) => CellRenderers.VALUE_OR_NA(record.subProductName),
        showFilter: true,
        width: 100
    },{
        title: "Branch",
        dataIndex: "branchName",
        render: CellRenderers.VALUE_OR_NA,
        width: 100
    },
     {
        title: "Action",
        width: 60,
        render: (record) =>  <ActionMenuV2 items={items(record)} onClick={onClick} data={record} />,
        className: "text-center",
    }
];

    const title = `${_.replace(toolName, "_", " ")} Repositories`;

    return (
        <TableContainerEvolved
            tableRef={getTableRef}
            columns={columns}
            api={fetchRepoListing}
            title={title}
            filtersApi={fetchRepoFilter}
            enableScroll
            showSizeChanger={true}
            disableURLUpdate={true}
        />
    )
}